
import React from 'react';

/**
 * Applies a validator function recursively to all child elements.
 * @param {Function} validatorFn - Function that accepts a React child and returns true if valid.
 * @param {Function} errorFn - Function that generates an error message.
 * @returns {Function} PropTypes validator for children.
 */
export function childTreeValidator(validatorFn, errorFn) {
  return function (props, propName, componentName) {
    const prop = props[propName];

    function isInvalidChild(child) {
      const isInvalid = !validatorFn(child);
      if (child && child.props && child.props.children) {
        return isInvalid || containsInvalidChild(child.props.children);
      }
      return isInvalid;
    }

    function containsInvalidChild(children) {
      return React.Children.toArray(children).some(isInvalidChild);
    }

    if (propName === 'children' && prop && containsInvalidChild(prop)) {
      return new Error(errorFn(componentName));
    }
    return null;
  };
}

/**
 * Auto-format a date string while typing.
 * @param {string} value
 * @returns {string} formatted date
 */
export function autoFormatDate(value) {
  return value
    .replace(/\.{2,}/g, '.') // replace multiple dots with single dot
    .replace(/^\.?$/g, '') // remove single dot
    .replace(/^(\d)\.$/g, '0$1.') // add leading zero for day
    .replace(/^(\d\d)$/g, '$1.') // add dot after day
    .replace(/^(\d\d)(\d)$/g, '$1.$2') // add dot between day and month
    .replace(/^(\d\d\.\d)\.$/g, '$1.') // add leading zero to month if needed
    .replace(/^(\d\d\.\d\d)$/g, '$1.') // add dot after month
    .replace(/^(\d\d\.\d\d)(\d)$/g, '$1.$2') // add dot between month and year
    .replace(/[^\d.]/g, ''); // remove non-numeric/non-dot chars
}

/**
 * Returns an array of valid React element children (flattens arrays).
 * Omits booleans, null, undefined, and text.
 */
export function getChildElements(children) {
  return React.Children.toArray(children).filter((child) => React.isValidElement(child));
}

/**
 * Returns an array of valid React element children of a specific type.
 */
export function getChildElementsOfType(children, type) {
  return getChildElements(children).filter((child) => child.type === type);
}

/**
 * Checks if a child DOM element is contained within a host DOM element.
 */
export function isElementContainedWithin(childElement, hostElement) {
  if (!(childElement instanceof Element) || !(hostElement instanceof Element)) {
    return false;
  }
  return hostElement.contains(childElement);
}

/**
 * Calls onBlur only if focus moves outside the host element.
 * Stops propagation of the blur event.
 */
export function handleBlurOutside(e, hostElement, onBlur, value) {
  const relatedTarget = e.relatedTarget || document.activeElement;
  e.stopPropagation();
  if (!isElementContainedWithin(relatedTarget, hostElement)) {
    onBlur(value);
  }
}
